import React from 'react';
import { Box, IconButton, Menu, MenuItem, Divider } from '@mui/material';
import { MoreVert } from '@mui/icons-material';

/**
 * Reusable menu component for experiment cards
 * Provides a modular way to add menu items with consistent styling
 */
const ExperimentCardMenu = ({ 
  menuItems = [], 
  iconButtonProps = {},
  menuProps = {}
}) => {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const buttonRef = React.useRef(null);
  
  const handleClick = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };
  
  const handleClose = () => {
    setAnchorEl(null);
  };
  
  const handleMenuItemClick = (event, onClick) => {
    event.stopPropagation();
    handleClose();
    // Pass the button element as the event target to maintain proper anchor
    if (onClick) {
      // Create a new event with the button as target
      const syntheticEvent = new MouseEvent('click', {
        bubbles: true,
        cancelable: true,
        view: window
      });
      
      // Use defineProperty to set currentTarget (it's read-only)
      Object.defineProperty(syntheticEvent, 'currentTarget', {
        value: buttonRef.current,
        enumerable: true,
        configurable: true
      });
      
      Object.defineProperty(syntheticEvent, 'target', {
        value: buttonRef.current,
        enumerable: true,
        configurable: true
      });
      
      onClick(syntheticEvent);
    }
  };
  
  return (
    <>
      <IconButton
        ref={buttonRef}
        size="small"
        onClick={handleClick}
        sx={{
          p: '3px',
          color: '#9ca3af',
          transition: 'opacity 0.1s ease',
          opacity: 0.6,
          '&:hover': {
            opacity: 1,
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
          },
          ...iconButtonProps.sx
        }}
        {...iconButtonProps}
      >
        <MoreVert sx={{ fontSize: '14px' }} />
      </IconButton>
      
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        onClick={(e) => e.stopPropagation()}
        {...menuProps}
      >
        {menuItems.map((item, index) => {
          if (item.divider) {
            return <Divider key={`divider-${index}`} />;
          }
          
          return (
            <MenuItem
              key={item.key || index}
              onClick={(e) => handleMenuItemClick(e, item.onClick)}
              disabled={item.disabled}
              sx={{
                fontSize: '12px',
                ...item.sx
              }}
            >
              {item.icon && (
                <Box sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>
                  {item.icon}
                </Box>
              )}
              {item.label}
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
};

export default ExperimentCardMenu;